import { useCallback, useEffect, useRef } from "react";
import { createTrackingUtils } from "..";
import { useHistory } from "react-router-dom";
import getRouteName from "utils/getRouteName";
import useAvailableCheckoutSteps from "hooks/checkout/useAvailableCheckoutSteps";
import useProfiler from "hooks/useProfiler";
import useGlobalContext from "hooks/useGlobalContext";

const utils = createTrackingUtils({
  name: "flights_web_checkout_back_navigation",
  stages: {
    triggers_check: 1,
    back_from_any_checkout_step: 2,
    back_from_after_second_step: 3, // main
    desktop: 4,
    mdot: 5,
    uk: 6,
    spanish_island: 7,
    used_browser_forward: 8
  },
  defaultStage: 1,

  goals: {},
  goalsWithValue: []
});

export const CheckoutBackNavigationTracker = () => {
  const history = useHistory();
  const steps = useAvailableCheckoutSteps();
  const { isDesktop, isMobile } = useProfiler();
  const { ipCountry } = useGlobalContext();

  const stackLimit = 3;
  const routesStack = useRef([getRouteName(window.location.pathname)]);
  const haveBeenTracked = useRef(false);

  const addToStack = (location: string) => {
    if (routesStack.current[routesStack.current.length - 1] === location) {
      return;
    }

    routesStack.current.push(location);

    if (routesStack.current.length > stackLimit) {
      routesStack.current.shift();
    }
  };

  const getPreviousRouteFromStack = () => {
    return routesStack.current[routesStack.current.length - 2];
  };

  const getCurrentRouteFromStack = () => {
    return routesStack.current[routesStack.current.length - 1];
  };

  const getFirstRouteFromStack = () => {
    return routesStack.current[0];
  };

  const isPOS = useCallback(
    (pos: string) => {
      const ip = ipCountry?.toLowerCase() || "";
      return ip === pos;
    },
    [ipCountry]
  );

  const isSIRFAvailable = useCallback(() => {
    return isPOS("es") && Boolean(steps.find(({ step }) => step === "checkout-sirf"));
  }, [isPOS, steps]);

  useEffect(() => {
    const unlisten = history.listen((location, action) => {
      addToStack(getRouteName(location.pathname));

      if (action === "POP") {
        const firstRoute = getFirstRouteFromStack();
        const prevRoute = getPreviousRouteFromStack();
        const currentRoute = getCurrentRouteFromStack();

        const prevRouteIndex = steps.findIndex(({ step }) => step === prevRoute);
        const currentRouteIndex = steps.findIndex(({ step }) => step === currentRoute);

        const shouldNotTrack = firstRoute.includes("confirmation") || firstRoute.includes("pb-");

        if (shouldNotTrack) return;

        if (prevRouteIndex >= 0) {
          utils.stages.back_from_any_checkout_step();
        }

        if (steps.length > 3) {
          if (prevRouteIndex > 2) {
            utils.stages.back_from_after_second_step();
            isDesktop() && utils.stages.desktop();
            isMobile() && utils.stages.mdot();
            isPOS("gb") && utils.stages.uk();
            isSIRFAvailable() && utils.stages.spanish_island();
            haveBeenTracked.current = true;
          }
        }

        if (haveBeenTracked.current && currentRouteIndex > prevRouteIndex) {
          utils.stages.used_browser_forward();
        }
      }
    });

    return () => {
      unlisten();
    };
  }, [history, isDesktop, isMobile, isPOS, isSIRFAvailable, steps]);

  return null;
};

export default utils;
